import { useMemo } from "react";
import { Clock } from "lucide-react";
import { useImageryStackStore } from "../../stores/useImageryStackStore";
import { formatAcquisitionDate } from "../../layers/imagery/formatImageryDate";
import { isFollowingCursor } from "../../layers/imagery/effectiveDate";

/**
 * Bottom-right pill that surfaces "follow mode" state. Renders only when
 * at least one imagery layer is in time_mode=follow_cursor. Gives users
 * peripheral awareness that the map is reading from the global cursor —
 * without this, the connection between pin/clock icons in the Active
 * Layers tray and on-map rendering is easy to miss.
 *
 * IT DOES NOT POSITION ITSELF. MapView renders it inside the map's bottom
 * stack, on the lat/lng readout's row and right-aligned, directly above the
 * time strip it describes. The stack is one flex column so no offset tracks
 * the strip's height. This pill used to sit top-center, where the case bar
 * and the map's top controls compete for room.
 */
export default function FollowModeHUD() {
  const stack = useImageryStackStore(s => s.stack);
  const cursorDate = useImageryStackStore(s => s.cursor.global_date);

  const followerCount = useMemo(
    // Via the shared predicate: addLayer writes "global", not "follow_cursor",
    // so a `=== "follow_cursor"` test counted freshly-added layers as zero.
    () => stack.filter(isFollowingCursor).length,
    [stack]
  );

  if (followerCount === 0) return null;

  return (
    <div className="pointer-events-none" data-follow-mode-hud>
      <div className={`
        flex items-center gap-2
        px-3 py-1
        bg-slate-900/80 backdrop-blur-sm
        border border-accent-info/40
        rounded-full
        shadow-lg shadow-black/30
      `}>
        <Clock size={11} className="text-accent-info shrink-0" />
        <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-accent-info font-medium">
          Follow Mode
        </span>
        <span className="text-[10px] text-gray-400">·</span>
        <span className="text-[10px] text-gray-300 tabular-nums">
          {followerCount} {followerCount === 1 ? "layer" : "layers"}
        </span>
        {cursorDate && (
          <>
            <span className="text-[10px] text-gray-400">·</span>
            <span className="text-[10px] text-accent-info font-medium tabular-nums">
              {formatAcquisitionDate(cursorDate)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}